import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-amber-50/30 to-gray-100">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-20 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://public.readdy.ai/ai/img_res/12b51af8-4eee-43c3-bc45-830da31fea8b.png" 
              alt="Sved d.o.o. Logo" 
              className="h-12 w-auto object-contain"
            />
            <h1 className="text-2xl font-bold text-slate-800">Sved d.o.o.</h1>
          </div>
          <div className="flex gap-8">
            <button onClick={() => scrollToSection('home')} className="text-slate-700 hover:text-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer">Home</button>
            <button onClick={() => scrollToSection('about')} className="text-slate-700 hover:text-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer">About</button>
            <button onClick={() => scrollToSection('products')} className="text-slate-700 hover:text-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer">Products</button>
            <button onClick={() => scrollToSection('features')} className="text-slate-700 hover:text-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer">Features</button>
            <button onClick={() => scrollToSection('contact')} className="text-slate-700 hover:text-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-40 px-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-block px-6 py-2 bg-purple-50 rounded-full">
                <span className="text-sm font-medium text-purple-700">iOS Development Experts</span>
              </div>
              <h2 className="text-6xl font-bold leading-tight">
                <span className="block text-slate-900 font-extrabold">Modern Applications</span>
                <span className="block text-slate-700 font-semibold mt-2">for Enhanced</span>
                <span className="block text-slate-900 font-extrabold mt-2">Mobile Experience</span>
              </h2>
              <p className="text-xl text-slate-600 leading-relaxed max-w-lg">
                We are a team of experts developing cutting-edge applications for the iOS platform. Our products, including VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-[32px] p-12 shadow-xl">
                <div className="flex gap-8 justify-center items-center">
                  <div className="transform rotate-[-8deg] transition-transform hover:rotate-[-4deg]">
                    <img 
                      src="https://public.readdy.ai/ai/img_res/c4b9829f7f01e0fb37bd53ebd87be32a.jpg" 
                      alt="VPN App Interface"
                      className="w-64 h-auto rounded-3xl shadow-2xl"
                    />
                  </div>
                  <div className="transform rotate-[8deg] transition-transform hover:rotate-[4deg]">
                    <img 
                      src="https://readdy.ai/api/search-image?query=elegant%20iPhone%20mockup%20showing%20cleaner%20optimization%20app%20dashboard%20with%20storage%20analytics%20and%20clean%20button%20on%20simple%20white%20background%20professional%20product%20shot%20minimal%20aesthetic&width=300&height=600&seq=hero-phone-2&orientation=portrait" 
                      alt="Cleaner App Interface"
                      className="w-64 h-auto rounded-3xl shadow-2xl"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-20 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-2 gap-20 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20software%20development%20team%20workspace%20with%20modern%20computers%20and%20iOS%20devices%20collaborative%20environment%20clean%20minimalist%20office%20design%20natural%20lighting%20technology%20atmosphere&width=600&height=500&seq=about-image&orientation=landscape" 
                alt="Our Team"
                className="w-full h-[500px] object-cover rounded-3xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <div className="inline-block px-5 py-2 bg-teal-50 rounded-full">
                <span className="text-sm font-semibold text-teal-700">About Us</span>
              </div>
              <h3 className="text-5xl font-bold text-slate-900 leading-tight">
                Crafting Excellence in iOS Development
              </h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                Sved d.o.o. is a dedicated team of iOS development specialists committed to creating innovative mobile solutions. We focus on delivering applications that not only meet but exceed user expectations in terms of functionality, security, and design.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Our expertise spans across various domains of mobile application development, with a particular emphasis on security and optimization tools. We believe in creating products that make a real difference in users' daily mobile experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 px-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-5 py-2 bg-blue-50 rounded-full mb-6">
              <span className="text-sm font-semibold text-blue-700">Our Products</span>
            </div>
            <h3 className="text-5xl font-bold text-slate-900">VPN & Cleaner Solutions</h3>
          </div>

          <div className="grid grid-cols-3 gap-8">
            {/* VPN Card */}
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-blue-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-4">Secure VPN Connection</h4>
              <p className="text-slate-600 leading-relaxed mb-6">
                Protect your online privacy with military-grade encryption. Our VPN ensures your data remains secure and your browsing stays private across all networks.
              </p>
              <img 
                src="https://readdy.ai/api/search-image?query=iPhone%20screen%20displaying%20VPN%20application%20interface%20with%20connection%20toggle%20server%20locations%20and%20security%20status%20on%20clean%20white%20background%20modern%20UI%20design&width=300&height=400&seq=vpn-product&orientation=portrait" 
                alt="VPN App"
                className="w-full h-64 object-cover rounded-2xl mt-4"
              />
            </div>

            {/* Statistics Card */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow flex flex-col justify-center items-center text-center row-span-1">
              <div className="text-7xl font-bold text-indigo-600 mb-4">99.9%</div>
              <h4 className="text-2xl font-bold text-slate-900 mb-4">Uptime Guarantee</h4>
              <p className="text-slate-600 leading-relaxed">
                Our infrastructure ensures maximum reliability and availability for all our services, keeping you connected when it matters most.
              </p>
            </div>

            {/* Cleaner Card */}
            <div className="bg-gradient-to-br from-teal-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-delete-bin-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-4">Smart Device Cleaner</h4>
              <p className="text-slate-600 leading-relaxed mb-6">
                Optimize your device performance with intelligent cleaning algorithms. Remove junk files, clear cache, and free up valuable storage space effortlessly.
              </p>
              <img 
                src="https://readdy.ai/api/search-image?query=iPhone%20displaying%20cleaner%20app%20dashboard%20with%20storage%20usage%20charts%20optimization%20buttons%20and%20clean%20interface%20on%20plain%20white%20background%20sleek%20modern%20design&width=300&height=400&seq=cleaner-product&orientation=portrait" 
                alt="Cleaner App"
                className="w-full h-64 object-cover rounded-2xl mt-4"
              />
            </div>

            {/* Privacy Card */}
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-purple-400">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-lock-line text-3xl text-purple-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-4">Privacy Protection</h4>
              <p className="text-slate-600 leading-relaxed">
                Your privacy is our priority. We implement advanced security measures to ensure your personal data remains confidential and protected.
              </p>
            </div>

            {/* One-Tap Card */}
            <div className="bg-gradient-to-br from-emerald-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-emerald-400 col-span-2">
              <div className="flex items-center gap-8">
                <div className="flex-shrink-0">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iPhone%20interface%20showing%20one%20tap%20optimization%20button%20with%20circular%20progress%20indicator%20and%20clean%20minimalist%20design%20on%20white%20background%20modern%20app%20UI&width=300&height=400&seq=optimization-feature&orientation=portrait" 
                    alt="One-Tap Optimization"
                    className="w-48 h-64 object-cover rounded-2xl"
                  />
                </div>
                <div className="flex-1">
                  <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-flashlight-line text-3xl text-emerald-600"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-slate-900 mb-4">One-Tap Optimization</h4>
                  <p className="text-slate-600 leading-relaxed">
                    Experience the convenience of optimizing your entire device with a single tap. Our smart algorithms analyze and clean your device automatically, saving you time and effort.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-20 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-5xl font-bold text-slate-900 mb-4">Built for Performance</h3>
            <p className="text-xl text-slate-600">Leveraging cutting-edge technology for optimal results</p>
          </div>

          <div className="grid grid-cols-4 gap-10">
            <div className="group text-center p-8 rounded-2xl hover:bg-teal-600 transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/20 transition-colors">
                <i className="ri-code-s-slash-line text-4xl text-gray-600 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-white">Swift Development</h4>
              <p className="text-slate-600 leading-relaxed group-hover:text-white/90">
                Built entirely with Swift for native iOS performance and seamless integration with Apple ecosystem.
              </p>
            </div>

            <div className="group text-center p-8 rounded-2xl hover:bg-teal-600 transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/20 transition-colors">
                <i className="ri-cloud-line text-4xl text-gray-600 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-white">CloudKit Integration</h4>
              <p className="text-slate-600 leading-relaxed group-hover:text-white/90">
                Seamless data synchronization across all your devices using Apple's CloudKit framework.
              </p>
            </div>

            <div className="group text-center p-8 rounded-2xl hover:bg-teal-600 transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/20 transition-colors">
                <i className="ri-shield-keyhole-line text-4xl text-gray-600 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-white">Advanced Encryption</h4>
              <p className="text-slate-600 leading-relaxed group-hover:text-white/90">
                Military-grade AES-256 encryption ensures your data remains secure and protected at all times.
              </p>
            </div>

            <div className="group text-center p-8 rounded-2xl hover:bg-teal-600 transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-white/20 transition-colors">
                <i className="ri-speed-line text-4xl text-gray-600 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-white">Optimized Performance</h4>
              <p className="text-slate-600 leading-relaxed group-hover:text-white/90">
                Lightweight architecture ensures minimal battery drain and maximum efficiency on your device.
              </p>
            </div>
          </div>

          <div className="mt-20 bg-gradient-to-r from-teal-50 to-blue-50 rounded-3xl p-12">
            <div className="grid grid-cols-2 gap-12 items-center">
              <div>
                <h4 className="text-4xl font-bold text-slate-900 mb-6">Why Choose Our Applications?</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <div>
                      <strong className="text-slate-900 block mb-1">User-Centric Design</strong>
                      <p className="text-slate-600">Intuitive interfaces designed with user experience as the top priority.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <div>
                      <strong className="text-slate-900 block mb-1">Regular Updates</strong>
                      <p className="text-slate-600">Continuous improvements and new features based on user feedback.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <div>
                      <strong className="text-slate-900 block mb-1">Dedicated Support</strong>
                      <p className="text-slate-600">Our team is always ready to assist you with any questions or concerns.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <div>
                      <strong className="text-slate-900 block mb-1">Privacy First</strong>
                      <p className="text-slate-600">We never collect or sell your personal data. Your privacy is guaranteed.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iPhone%20devices%20displaying%20mobile%20applications%20with%20security%20and%20optimization%20features%20arranged%20elegantly%20on%20clean%20white%20surface%20professional%20product%20photography&width=600&height=500&seq=why-choose&orientation=landscape" 
                  alt="Our Applications"
                  className="w-full h-[400px] object-cover rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-20 bg-gradient-to-b from-slate-800 to-slate-900">
        <div className="max-w-[1400px] mx-auto">
          <div className="bg-slate-700/50 rounded-[32px] p-16">
            <div className="grid grid-cols-2 gap-16">
              {/* Contact Form */}
              <div>
                <h3 className="text-4xl font-bold text-white mb-4">Get in Touch</h3>
                <p className="text-slate-300 mb-8 text-lg">Have questions about our products? We'd love to hear from you.</p>
                
                <form id="contact-form" data-readdy-form action="https://readdy.ai/api/form/d6arr2tbiueuoqm1smqg" method="POST" className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-white font-medium mb-2">Name</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      required
                      className="w-full px-4 py-3 rounded-lg bg-slate-600/50 border border-slate-500 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-white font-medium mb-2">Email</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      required
                      className="w-full px-4 py-3 rounded-lg bg-slate-600/50 border border-slate-500 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-white font-medium mb-2">Message</label>
                    <textarea 
                      id="message" 
                      name="message" 
                      rows={5}
                      maxLength={500}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-slate-600/50 border border-slate-500 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      placeholder="Your message (max 500 characters)"
                    ></textarea>
                    <p className="text-slate-400 text-sm mt-1">Maximum 500 characters</p>
                  </div>
                  <button 
                    type="submit"
                    className="w-full px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Send Message
                  </button>
                </form>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h4 className="text-2xl font-bold text-white mb-6">Contact Information</h4>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-teal-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-2xl text-teal-400"></i>
                      </div>
                      <div>
                        <div className="text-sm text-slate-400 uppercase tracking-wide mb-2">Location</div>
                        <div className="text-white text-lg">Branilaca Tuzle 61</div>
                        <div className="text-white text-lg">Tuzla, 75000</div>
                        <div className="text-white text-lg">Bosnia & Herzegovina</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-teal-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-2xl text-teal-400"></i>
                      </div>
                      <div>
                        <div className="text-sm text-slate-400 uppercase tracking-wide mb-2">Email</div>
                        <a href="mailto:info@sveddoo.com" className="text-white text-lg hover:text-teal-400 transition-colors cursor-pointer">
                          info@sveddoo.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-teal-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-2xl text-teal-400"></i>
                      </div>
                      <div>
                        <div className="text-sm text-slate-400 uppercase tracking-wide mb-2">Phone</div>
                        <a href="tel:+19403021014" className="text-white text-lg hover:text-teal-400 transition-colors cursor-pointer">
                          +1 9403021014
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-8 border-t border-slate-600">
                  <h4 className="text-xl font-bold text-white mb-4">Business Hours</h4>
                  <div className="space-y-2 text-slate-300">
                    <div className="flex justify-between">
                      <span>Monday - Friday</span>
                      <span>9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday</span>
                      <span>10:00 AM - 4:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday</span>
                      <span>Closed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-16 pt-8 border-t border-slate-700">
            <div className="flex justify-between items-center">
              <p className="text-slate-400">© 2025 Sved d.o.o. All rights reserved</p>
              <div className="flex gap-4">
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors text-sm cursor-pointer">
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
